import { BaseText, Headline3, Meta, Spinner, theme } from 'aimo-ui';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import ParkingContext from 'context/ParkingContext';
import useHasPermitForZone from 'hooks/useHasPermitForZone';
import usePermitParkZone from 'hooks/usePermitParkZone';
import useSelectParkingZone from 'hooks/useSelectParkingZone';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkingStore } from 'stores/parkingStore';
import styled from 'styled-components/native';
import { Pill } from 'styles/CommonLayoutStyles';
import { ColumnWithMargins, SimpleFullContainer } from 'styles/ContainerStyles';
import { CountryCode } from 'types/generatedSchemaTypes';
import { LatLng } from 'types/MapTypes';
import { getDistanceText, getFormattedAddress } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getDistanceFromLatLonInMeters, makePoint } from 'utils/mapUtils';
import { getLocationIconType, locationIcon } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import TouchableItem from '../common/TouchableItem';
import Icon from '../icons/Icon';
import AddressAndLocation, {
  formatLocationTextForScreenReader,
} from './AddressAndLocation';
import CapacityInfo from './CapacityInfo';
import { ParkZoneWithVisibility } from './ParkingZoneSearch';
import PoolingBadge from './PoolingBadge';
import { IconWrapper, Row } from './styles/styles';

const ItemRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ItemColumn = styled.View<{ shrink?: boolean; ml?: number }>`
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const AddressWrapper = styled.View`
  flex: 0 1 100%;
`;

const HeadlineShrink = styled(Headline3)`
  flex-shrink: 1;
`;

const SearchItem: React.FC<{
  item: ParkZoneWithVisibility;
  currentLocation: LatLng | null;
  withLTP?: boolean;
}> = ({ item, currentLocation, withLTP = false }) => {
  const { handleParkZoneSelected } = useSelectParkingZone();
  const { selectedParkingZoneId } = useParkingStore(
    (state) => state.parkingConditions
  );
  const {
    uid: parkZoneUid,
    address,
    commercialZoneView,
    name,
    parkingMethods,
    countryCode,
    memberGroupName,
  } = item ?? {};
  const capacity = item.capacityV2;

  const { t } = useTranslation();

  const coordinates = address?.marker?.coordinates;
  const locationType = item?.locationType ?? 'OTHER';
  const isStreet = locationType === 'STREET' && countryCode !== 'SE';
  const permitForParkZone = usePermitParkZone(parkZoneUid);
  const locationText =
    currentLocation &&
    coordinates &&
    getDistanceText(
      getDistanceFromLatLonInMeters(currentLocation, makePoint(coordinates)) /
        1000,
      countryCode as CountryCode
    );

  // Use the imported function from AddressAndLocation with the translation function
  const screenReaderLocationText = formatLocationTextForScreenReader(
    locationText,
    t
  );

  const {
    flags: { anpr },
  } = useContext(FeatureFlagsContext);
  const showCameraRecognition = showFeatureFlag(anpr);

  const locationIconName = getLocationIconType(
    locationType ?? 'OTHER',
    showCameraRecognition && parkingMethods?.anpr,
    parkingMethods?.unmanagedParking
  );

  const { isLoadingParkZoneDetails } = useContext(ParkingContext);

  const hasPermitForZone = useHasPermitForZone(parkZoneUid);

  const directToChargeSelect =
    !!parkingMethods?.evCharging &&
    !!parkingMethods?.unmanagedParking &&
    hasPermitForZone;

  const onPressMarker = () => {
    if (parkZoneUid) {
      handleParkZoneSelected(
        parkZoneUid,
        coordinates ? makePoint(coordinates) : undefined,
        withLTP,
        permitForParkZone?.orderLineIdWithVersion ?? permitForParkZone?.id,
        isStreet,
        directToChargeSelect
      );
    }
  };

  const isParkAndCharge =
    !parkingMethods?.unmanagedParking && parkingMethods?.evCharging;

  const rowMargin = 0;

  // Create accessibility label for the parking location
  const parkingName = commercialZoneView?.name ?? name ?? '';
  const zoneCodeLabel = commercialZoneView?.zoneCode
    ? `${t('ariaLabels.parkingLocation.zone')} ${commercialZoneView.zoneCode}`
    : '';

  const addressString = getFormattedAddress(address);
  const distanceInfo = screenReaderLocationText
    ? `, ${screenReaderLocationText}`
    : '';
  const locationTypeLabel =
    `${t('ariaLabels.parkingLocation.locationType')} ${locationIconName} ` +
    (isParkAndCharge ? t('ariaLabels.parkingLocation.withCharging') : '');
  const memberGroupLabel = memberGroupName
    ? `${t('ariaLabels.parkingLocation.memberGroup')} ${memberGroupName}`
    : '';
  const accessibilityLabel = `${zoneCodeLabel}${parkingName}, ${addressString}${distanceInfo}. ${locationTypeLabel}${memberGroupLabel}`;

  return (
    <TouchableItem
      disabled={isLoadingParkZoneDetails}
      onPress={onPressMarker}
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      {...testIdentifiers(`search-item-${parkZoneUid}`)}>
      <ItemRow>
        <ItemColumn>
          <Row mt={rowMargin} mb={rowMargin}>
            {!!commercialZoneView?.zoneCode && (
              <ColumnWithMargins mr={5}>
                <BaseText
                  color={theme.colors.boost.green}
                  {...testIdentifiers('search-item-zone-code')}>
                  {commercialZoneView?.zoneCode || ''}
                </BaseText>
              </ColumnWithMargins>
            )}
            <HeadlineShrink
              numberOfLines={1}
              {...testIdentifiers('search-item-name')}>
              {commercialZoneView?.name || name}
            </HeadlineShrink>
          </Row>
          <Row mt={rowMargin} mb={rowMargin}>
            <IconWrapper mr={3}>
              <Icon
                name={locationIcon[locationIconName]}
                size="s"
                testID={`location-icon-${locationIconName}`}
              />
            </IconWrapper>
            {isParkAndCharge && (
              <IconWrapper mr={3}>
                <Icon
                  name={locationIcon.evCharging}
                  size="s"
                  testID={'location-icon-parkAndCharge'}
                />
              </IconWrapper>
            )}
            <AddressWrapper>
              <AddressAndLocation
                color={theme.colors.gray300}
                numberOfLines={2}
                name={commercialZoneView?.name || name || ''}
                address={address}
                testID="search-street-location"
                locationText={locationText}
                includeCity={false}
                isStreetZone={isStreet}
              />
            </AddressWrapper>
          </Row>
          {capacity && (
            <CapacityInfo capacity={capacity} testID="capacity-info" />
          )}
          {!withLTP && (
            <Row mt={0} mb={0}>
              <PoolingBadge parkingZoneUid={parkZoneUid} />
            </Row>
          )}
        </ItemColumn>
        <ColumnWithMargins
          ml={10}
          {...testIdentifiers(`park-button-${parkZoneUid}`)}>
          {isLoadingParkZoneDetails && selectedParkingZoneId === parkZoneUid ? (
            <Spinner small />
          ) : (
            <Icon size={'m'} name="Chevron_Right" />
          )}
        </ColumnWithMargins>
      </ItemRow>
      {memberGroupName && (
        <ItemRow>
          <SimpleFullContainer>
            <Pill mt={5}>
              <Meta>{memberGroupName}</Meta>
            </Pill>
          </SimpleFullContainer>
        </ItemRow>
      )}
    </TouchableItem>
  );
};

export default SearchItem;
